import { LoaderCircle } from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import { corregirRecepcionValidada } from '../../services/abastecimiento'
import { validarCorreccionRecepcion } from '../../utils/abastecimiento'
import { formatearKilosCompra } from '../../utils/compras'
import ModalBase, {
  AvisoModal,
  CampoModal,
  estiloInputModal,
} from './ModalBase'

function CorregirRecepcionModal({
  abierto,
  compra,
  recepcion,
  empresaId,
  onCerrar,
  onCorregida,
}) {
  const [kilosNuevos, setKilosNuevos] = useState('')
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')
  const [procesando, setProcesando] = useState(false)
  const enCursoRef = useRef(false)

  useEffect(() => {
    if (!abierto) return

    setKilosNuevos(String(recepcion?.kg_aceptados ?? '').replace('.', ','))
    setMotivo('')
    setError('')
    enCursoRef.current = false
  }, [abierto, recepcion])

  if (!abierto || !compra || !recepcion) return null

  const alCorregir = async () => {
    if (enCursoRef.current) return

    const resultado = validarCorreccionRecepcion({
      kilosNuevos,
      kilosActuales: recepcion.kg_aceptados,
      netoKg: compra.peso_neto_kg,
      motivo,
    })

    if (resultado.error) {
      setError(resultado.error)
      return
    }

    enCursoRef.current = true
    setProcesando(true)
    setError('')

    try {
      await corregirRecepcionValidada({
        empresaId,
        recepcionId: recepcion.id,
        kilosNuevos: resultado.kilos,
        motivo: motivo.trim(),
      })

      onCorregida()
    } catch (errorCorreccion) {
      // Ej.: stock insuficiente. No se cambia nada en pantalla.
      console.error('Error al corregir recepción:', errorCorreccion)
      setError(
        errorCorreccion.message || 'No fue posible corregir la recepción.'
      )
    } finally {
      enCursoRef.current = false
      setProcesando(false)
    }
  }

  return (
    <ModalBase
      abierto={abierto}
      titulo="Corregir recepción validada"
      tituloId="corregir-recepcion-titulo"
      procesando={procesando}
      anchoMaximo="max-w-lg"
      onCerrar={onCerrar}
    >
      <p className="mt-2 text-sm text-slate-500">
        {compra.codigo_compra} · neto recibido{' '}
        {formatearKilosCompra(compra.peso_neto_kg)} · aceptados actuales{' '}
        {formatearKilosCompra(recepcion.kg_aceptados)}
      </p>

      <div className="mt-5 space-y-4">
        <AvisoModal tipo="info">
          Se registra un movimiento compensatorio de inventario. El
          movimiento original se conserva para auditoría.
        </AvisoModal>

        <CampoModal
          label="Nuevos kilos aceptados"
          htmlFor="corregir-kilos"
          requerido
        >
          <input
            id="corregir-kilos"
            type="text"
            inputMode="decimal"
            value={kilosNuevos}
            onChange={(event) => {
              setKilosNuevos(event.target.value)
              setError('')
            }}
            disabled={procesando}
            className={estiloInputModal}
          />
        </CampoModal>

        <CampoModal
          label="Motivo de la corrección"
          htmlFor="corregir-motivo"
          requerido
        >
          <textarea
            id="corregir-motivo"
            value={motivo}
            onChange={(event) => {
              setMotivo(event.target.value)
              setError('')
            }}
            rows={3}
            maxLength={1000}
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
          />
        </CampoModal>

        {error && <AvisoModal>{error}</AvisoModal>}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            disabled={procesando}
            className="min-h-12 w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={alCorregir}
            disabled={procesando}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:bg-emerald-400 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Corrigiendo...' : 'Corregir recepción'}
          </button>
        </div>
      </div>
    </ModalBase>
  )
}

export default CorregirRecepcionModal
